import ApiService, { HttpMethod } from "../../../../commons/api/api_service";
import BaseResponse from "../../../../commons/api/base_response";
import { Endpoints } from "../../../../commons/api/endpoints";
import { ProviderVM } from "../../../../commons/base/providers/view_model";
import { ShowSnackbarModel } from "../../../../commons/scaffold/scaffold";
import LocationModel from "../../commons/models/location_model";
import MasterItemModel from "../../commons/models/master_item_model";
import AdjustableWasteFormResponse from "../models/adjustable_waste_form_response";
import WasteAdjustmentSubmitRequest from "../models/waste_adjustment_submit_request";
import { WasteFormDetailResponse } from "../models/waste_form_detail_response";
import WasteItemQuantityModel from "../models/waste_item_quantity_model";
import WastePreloadingResponse from "../models/waste_preloading_response";
import WasteTypeModel from "../models/waste_type_model";

export default class WasteAdjustmentViewModel extends ProviderVM {
    isLoading: boolean = false;
    snackBar?: ShowSnackbarModel;

    locations: LocationModel[] = [];
    wasteTypes: WasteTypeModel[] = [];
    forms: AdjustableWasteFormResponse[] = [];
    masterItemMap: Map<number, MasterItemModel> = new Map<
        number,
        MasterItemModel
    >();

    // form data
    isShowingModal: boolean = false;
    searchQuery: string = "";
    selectedItemId: number = 0;
    formId?: number;
    formItemsMap: Map<number, WasteItemQuantityModel> = new Map<
        number,
        WasteItemQuantityModel
    >();

    constructor() {
        super();
        this.fetchWastePreloading();
    }

    get shownMasterItems() {
        const masterItemIdsInForm = Array.from(this.formItemsMap.values()).map(
            (item) => item.masterItemId
        );

        return Array.from(this.masterItemMap.values()).filter((item) =>
            masterItemIdsInForm.includes(item.id)
        );
    }

    get filteredMasterItems() {
        return this.shownMasterItems.filter((item) =>
            item.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
    }

    get filteredItemQtyModels() {
        const filteredMasterItemIds = this.filteredMasterItems.map(
            (item) => item.id
        );

        return Array.from(this.formItemsMap.values()).filter((item) =>
            filteredMasterItemIds.includes(item.masterItemId)
        );
    }

    get submitableItems() {
        return Array.from(this.formItemsMap.values()).filter(
            (item) => item.isUpdated
        );
    }

    get isAbleToSubmit() {
        return Array.from(this.formItemsMap.values()).every(
            (item) => item.wasteType.length > 0
        );
    }

    get shownItemOptions() {
        const masterItemIdsInForm = Array.from(this.formItemsMap.values()).map(
            (item) => item.masterItemId
        );

        return Array.from(this.masterItemMap.values()).filter(
            (item) => !masterItemIdsInForm.includes(item.id)
        );
    }

    updateSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    updateSelectedItemId(value: number) {
        this.emit(() => {
            this.selectedItemId = value;
        });
    }

    fetchWastePreloading() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.newWasteAdjustmentPreloading, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<WastePreloadingResponse>) => {
                if (response.code === 200) {
                    const masterItemMap = new Map<number, MasterItemModel>();

                    response.data?.items.forEach((item) => {
                        masterItemMap.set(item.id, item);
                    });

                    this.emit(() => {
                        this.isLoading = false;
                        this.forms = response.data?.forms ?? [];
                        this.locations = response.data?.locations ?? [];
                        this.wasteTypes = response.data?.wasteTypes ?? [];
                        this.masterItemMap = masterItemMap;
                    });
                } else {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: response.message,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                }
            },
            onFailure: (error, errorCode) => {
                this.emit(() => {
                    this.snackBar = {
                        key: Date.now().toString(),
                        message: error,
                        type: "error",
                    };
                    this.isLoading = false;
                });
            },
        });
    }

    fetchWasteList(locationId: number) {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(
            `${Endpoints.newWasteAdjustmentGetForms}/${locationId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<AdjustableWasteFormResponse[]>
                ) => {
                    if (response.code === 200) {
                        this.emit(() => {
                            this.isLoading = false;
                            this.forms = response.data ?? [];
                        });
                    } else {
                        this.emit(() => {
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "error",
                            };
                            this.isLoading = false;
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: error,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                },
            }
        );
    }

    fetchWasteDetail(wasteId: number) {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(
            `${Endpoints.newWasteAdjustmentGetFormDetail}/${wasteId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<WasteFormDetailResponse>
                ) => {
                    if (response.code === 200) {
                        const responseData: WasteFormDetailResponse =
                            response.data as WasteFormDetailResponse;

                        const formItemsMap: Map<
                            number,
                            WasteItemQuantityModel
                        > = new Map<number, WasteItemQuantityModel>();

                        response.data?.items.forEach((item) => {
                            formItemsMap.set(item.masterItemId, {
                                masterItemId: item.masterItemId,
                                qtyBigUnit: item.qtyBigUnit ?? 0,
                                qtySmallUnit: item.qtySmallUnit ?? 0,
                                wasteType: item.wasteType,
                                note: item.note,
                            });
                        });

                        this.emit(() => {
                            this.isLoading = false;
                            this.isShowingModal = true;
                            this.formId = responseData.formId;
                            this.formItemsMap = formItemsMap;
                        });
                    } else {
                        this.emit(() => {
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "error",
                            };
                            this.isLoading = false;
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: error,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                },
            }
        );
    }

    submitWasteAdjustment() {
        this.emit(() => {
            this.isLoading = true;
        });

        for (const item of [...this.submitableItems.values()]) {
            if (!item.wasteType) {
                this.emit(() => {
                    this.isLoading = false;
                    this.snackBar = {
                        key: Date.now().toString(),
                        type: "error",
                        message: "Kategori waste harus dipilih",
                    };
                });

                return;
            }
        }

        const submitParams: WasteAdjustmentSubmitRequest = {
            items: this.submitableItems.map((item) => ({
                masterItemId: item.masterItemId,
                qtyBigUnit: item.qtyBigUnit ?? 0,
                qtySmallUnit: item.qtySmallUnit ?? 0,
                note: item.note,
                wasteType: item.wasteType,
            })),
        };

        ApiService.fetch(
            `${Endpoints.newWasteAdjustmentSubmit}/${this.formId}` as Endpoints,
            {
                httpMethod: HttpMethod.post,
                parameters: submitParams,
                onSuccess: (response: BaseResponse<{}>) => {
                    if (response.code === 200) {
                        this.emit(() => {
                            this.isLoading = false;
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "normal",
                            };
                            this.isShowingModal = false;
                        });

                        this.fetchWastePreloading();
                    } else {
                        this.emit(() => {
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "error",
                            };
                            this.isLoading = false;
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: error,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                },
            }
        );
    }

    addItemQtyModel(masterItemId: number) {
        const updatedFormItemsMap = new Map(this.formItemsMap);

        if (!updatedFormItemsMap.get(masterItemId)) {
            updatedFormItemsMap.set(masterItemId, {
                masterItemId: masterItemId,
                qtyBigUnit: 0,
                qtySmallUnit: 0,
                isUpdated: true,
                note: "",
                wasteType: "",
            });
        }

        this.emit(() => {
            this.formItemsMap = updatedFormItemsMap;
        });
    }

    removeItemQtyModel(masterItemId: number) {
        const updatedFormItemsMap = new Map(this.formItemsMap);
        updatedFormItemsMap.delete(masterItemId);
        this.emit(() => {
            this.formItemsMap = updatedFormItemsMap;
        });
    }

    updateItemQtyModel(
        masterItemId: number,
        field: keyof WasteItemQuantityModel,
        value: number | string
    ) {
        const updatedFormItemsMap = new Map(this.formItemsMap);
        const oldItemQtyModel = updatedFormItemsMap.get(masterItemId);

        if (oldItemQtyModel) {
            updatedFormItemsMap.set(masterItemId, {
                ...oldItemQtyModel,
                isUpdated: true,
                [field]: value,
            });
        }

        this.emit(() => {
            this.formItemsMap = updatedFormItemsMap;
        });
    }

    resetForm() {
        this.emit(() => {
            this.isShowingModal = false;
            this.formId = undefined;
            this.formItemsMap = new Map<number, WasteItemQuantityModel>();
            this.selectedItemId = 0;
        });
    }
}
